import { useRef, useState, useEffect } from "react";
import {
  IonContent,
  IonHeader,
  IonPage,
  IonTitle,
  IonToolbar,
  IonIcon,
  IonLabel,
  IonList,
  IonItem,
  IonNote,
  useIonAlert,
  IonGrid,
  IonRow,
  IonCol,
  IonButtons,
  IonModal,
  IonButton,
  IonTextarea,
} from "@ionic/react";
import { useHistory } from "react-router-dom";
import {
  cardOutline,
  hammerOutline,
  infiniteOutline,
  informationCircleOutline,
  keyOutline,
  logOutOutline,
  megaphoneOutline,
  walletOutline,
  languageOutline,
} from "ionicons/icons";
import Session from "../../store/session";
import useScreenSize, { BreakPoint } from "../../hooks/useScreenSize";
import Constants from "../../utils/constants";
import npoService from "../../services/npo";
import useStore from "../../store/store";
import HeaderNavbar from "../../components/HeaderNavbar";
import FeedbackPopup from "../../components/FeedbackPopup";
import Utils from "../../utils/utils";
import ReactGA from "react-ga4";
import LanguagePopup from "../../components/LanguagePopup";
import { useTranslation } from "react-i18next";

const Settings = () => {
  const { user, setUser, token, setIsLoading } = useStore(
    (state: any) => state
  );
  const [presentAlert] = useIonAlert();
  const size = useScreenSize();
  const history = useHistory();
  const modal = useRef<HTMLIonModalElement>(null);
  const [id, setId] = useState<string>(user.paypal || "");
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [successMessage, setSuccessMessage] = useState<string>("");
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] =
    useState<boolean>(false);
  const { t, i18n } = useTranslation();
  const { setLanguage } = useStore((state: any) => state);
  const [isOpenLanguage, setIsOpenLanguage] = useState<boolean>(false);

  useEffect(() => {
    ReactGA.send({ hitType: "pageview", page: window.location.href });
  }, []);

  const confirm = () => {
    if (!id || !Utils.isValidEmail(id)) {
      setErrorMessage(t("emailShouldValid"));
      return;
    }
    modal.current?.dismiss(id, "confirm");
  };

  const onWillDismiss = (ev: any) => {
    if (ev.detail.role === "confirm") {
      setIsLoading(true);
      npoService.updatePaypal(token, { paypal: id }).then((result) => {
        if (result.error) {
          setSuccessMessage("");
          setErrorMessage(result.error);
          setIsLoading(false);
          return;
        }

        setUser(result.data);
        setErrorMessage("");
        setSuccessMessage(t("settingsUpdateSuccess"));
        setIsLoading(false);
      });
    }
  };

  const handleLogout = () => {
    presentAlert({
      header: t("sureLogout"),
      buttons: [
        {
          text: t("cancel"),
          role: "cancel",
          handler: () => {},
        },
        {
          text: t("logout"),
          role: "confirm",
          handler: () => {
            Session.logout();
            window.location.href = "/login";
          },
        },
      ],
    });
  };

  return (
    <>
      <LanguagePopup
        isOpen={isOpenLanguage}
        onClose={() => setIsOpenLanguage(false)}
        onChange={(lang: string) => {
          i18n.changeLanguage(lang);
          setLanguage(lang);
          setIsOpenLanguage(false);
        }}
      />
      <IonPage>
        <IonHeader>
          <IonToolbar>
            <IonTitle>{t("settings")}</IonTitle>
            {size.screen !== BreakPoint.xs && size.screen !== BreakPoint.s && (
              <HeaderNavbar />
            )}
          </IonToolbar>
        </IonHeader>
        <IonContent fullscreen>
          <IonHeader collapse="condense">
            <IonToolbar>
              <IonTitle size="large" className="ios-page-heading">
                {t("settings")}
              </IonTitle>
            </IonToolbar>
          </IonHeader>
          <IonGrid
            style={{
              padding: 0,
              maxWidth:
                size.screen === BreakPoint.xs || size.screen === BreakPoint.s
                  ? "100%"
                  : Constants.MAX_GRID_WIDTH,
            }}
          >
            <IonRow>
              <IonCol size="12">
                <IonList>
                  <IonItem
                    detail={true}
                    button
                    onClick={(e) => history.push("/preferences")}
                  >
                    <IonIcon icon={hammerOutline} slot="start" />
                    <IonLabel>
                      <h3>{t("preferences")}</h3>
                      <IonNote
                        slot="helper"
                        className="ion-text-wrap fwfs-500-14"
                      >
                        {t("accountPreferences")}
                      </IonNote>
                    </IonLabel>
                  </IonItem>
                  <IonItem button detail={true} id="open-modal">
                    <IonIcon icon={cardOutline} slot="start" />
                    <IonLabel>
                      <h3>{t("receivingAccount")}</h3>
                      <IonNote
                        slot="helper"
                        className="ion-text-wrap fwfs-500-14"
                      >
                        {t("setupPaypal")}
                      </IonNote>
                    </IonLabel>
                  </IonItem>
                  <IonItem
                    button
                    detail={true}
                    onClick={(e) => history.push("/transactions")}
                  >
                    <IonIcon icon={walletOutline} slot="start" />
                    <IonLabel>
                      <h3>{t("receivedDonations")}</h3>
                      <IonNote
                        slot="helper"
                        className="ion-text-wrap fwfs-500-14"
                      >
                        {t("listOfDonations")}
                      </IonNote>
                    </IonLabel>
                  </IonItem>

                  <IonItem
                    detail={true}
                    button
                    onClick={(e) => history.push("/subscriptions")}
                  >
                    <IonIcon icon={infiniteOutline} slot="start" />
                    <IonLabel>
                      <h3>{t("subscriptions")}</h3>
                      <IonNote
                        slot="helper"
                        className="ion-text-wrap fwfs-500-14"
                      >
                        {t("listOfSubscriptions")}
                      </IonNote>
                    </IonLabel>
                  </IonItem>
                  <IonItem
                    detail={true}
                    button
                    onClick={(e) => setIsOpenLanguage(true)}
                  >
                    <IonIcon icon={languageOutline} slot="start" />
                    <IonLabel>
                      <h3>{t("switchLanguage")}</h3>
                      <IonNote
                        slot="helper"
                        className="ion-text-wrap fwfs-500-14"
                      >
                        {t("switchLanguageHint")}
                      </IonNote>
                    </IonLabel>
                  </IonItem>
                  <IonItem
                    detail={true}
                    button
                    onClick={(e) => history.push("/update-password")}
                  >
                    <IonIcon icon={keyOutline} slot="start" />
                    <IonLabel>
                      <h3>{t("changePassword")}</h3>
                      <IonNote
                        slot="helper"
                        className="ion-text-wrap fwfs-500-14"
                      >
                        {t("changeYourAccountPassword")}
                      </IonNote>
                    </IonLabel>
                  </IonItem>
                  <IonItem
                    detail={true}
                    button
                    onClick={(e) => history.push("/qa")}
                  >
                    <IonIcon icon={informationCircleOutline} slot="start" />
                    <IonLabel>
                      <h3>{t("qa")}</h3>
                      <IonNote
                        slot="helper"
                        className="ion-text-wrap fwfs-500-14"
                      >
                        {process.env.REACT_APP_NAME} {t("faqs")}
                      </IonNote>
                    </IonLabel>
                  </IonItem>
                  <IonItem
                    detail={true}
                    button
                    onClick={() => setIsFeedbackModalOpen(true)}
                  >
                    <IonIcon icon={megaphoneOutline} slot="start" />
                    <IonLabel>
                      <h3>{t("feedback")}</h3>
                      <IonNote
                        slot="helper"
                        className="ion-text-wrap fwfs-500-14"
                      >
                        {t("feedbackHint")}
                      </IonNote>
                    </IonLabel>
                  </IonItem>
                  <IonItem button onClick={handleLogout}>
                    <IonIcon icon={logOutOutline} slot="start" color="danger" />
                    <IonLabel>
                      <h3>{t("logout")}</h3>
                      <IonNote
                        slot="helper"
                        className="ion-text-wrap fwfs-500-14"
                      >
                        {t("logoutHint")}
                      </IonNote>
                    </IonLabel>
                  </IonItem>
                </IonList>
              </IonCol>
            </IonRow>
          </IonGrid>

          <IonModal
            ref={modal}
            trigger="open-modal"
            onWillDismiss={(ev) => onWillDismiss(ev)}
          >
            <IonHeader>
              <IonToolbar>
                <IonButtons slot="start">
                  <IonButton onClick={() => modal.current?.dismiss()}>
                    {t("cancel")}
                  </IonButton>
                </IonButtons>
                <IonTitle>{t("paypalEmailAddress")}</IonTitle>
                <IonButtons slot="end">
                  <IonButton
                    strong={true}
                    color="primary"
                    onClick={() => confirm()}
                  >
                    {t("confirm")}
                  </IonButton>
                </IonButtons>
              </IonToolbar>
            </IonHeader>
            <IonContent className="ion-padding">
              <IonLabel>{t("paypalEmailAddressHint")}</IonLabel>

              <IonItem className="mt-30" fill="outline">
                <IonLabel position="floating">{t("email")}</IonLabel>
                <IonTextarea
                  required={true}
                  autofocus={true}
                  placeholder={t("email")}
                  value={id}
                  onIonChange={(e: any) => {
                    e.preventDefault();
                    setErrorMessage("");
                    setId(e.target.value);
                  }}
                />
              </IonItem>

              {errorMessage && (
                <IonLabel color="danger">{errorMessage}</IonLabel>
              )}
              {successMessage && (
                <IonLabel color="primary">{successMessage}</IonLabel>
              )}
            </IonContent>
          </IonModal>

          <FeedbackPopup
            isOpen={isFeedbackModalOpen}
            onClose={() => setIsFeedbackModalOpen(false)}
          />
        </IonContent>
      </IonPage>
    </>
  );
};

export default Settings;
